import { getStorageItem, setStorageItem, WebStorageType } from "../../../common-deprecated/utils/storageUtils";
import type { UscContext } from "../../shared-logic/types/UscCommonTypes";
import type { UscStateType } from "../redux/store";
import type { BasketConfigurationType } from "./basketUtils";

// ----------------------------------------------------------------------
// Helper types and consts
// ----------------------------------------------------------------------
const LOCAL_STORAGE_ID = "com.toyota.tme.t1.usedStockCars";

export type UscLocalStorageItemType = {
    visitedVehicleIds?: string[];
    basketConfigurations?: {
        [vehicleForSaleId: string]: BasketConfigurationType;
    };
};

export type UscLocalStorageParams = {
    context: UscContext;
    country: string;
    language: string;
};

// ----------------------------------------------------------------------
// Storage fns
// ----------------------------------------------------------------------
export const getUscLocalStorage = (params: UscLocalStorageParams): UscLocalStorageItemType | null => {
    return getStorageItem({ ...params, storageId: LOCAL_STORAGE_ID }, WebStorageType.local);
};

export const setUscLocalStorage = (params: UscLocalStorageParams, storageItem: UscLocalStorageItemType): void => {
    setStorageItem({ ...params, storageId: LOCAL_STORAGE_ID }, storageItem, WebStorageType.local);
};

export const getVisitedVehicleIds = (params: UscLocalStorageParams): string[] => {
    return getUscLocalStorage(params)?.visitedVehicleIds || [];
};

export const addVisitedVehicleId = (params: UscLocalStorageParams, vehicleForSaleId: string): void => {
    const visitedVehicleIds = getVisitedVehicleIds(params);
    visitedVehicleIds.push(vehicleForSaleId);

    setUscLocalStorage(params, { visitedVehicleIds });
};

// Basket configuration
export const setUscBasketConfiguration = (
    params: UscLocalStorageParams,
    configuration: BasketConfigurationType,
    vehicleForSaleId: string,
): void => {
    // We don't want to override configurations of other vehicles,
    // so we merge the new configuration with the existing one.
    const existingConfigurations = getAllUscBasketConfigurations(params);
    const updatedConfiguration = {
        ...existingConfigurations,
        [vehicleForSaleId]: configuration,
    };

    setUscLocalStorage(params, { basketConfigurations: updatedConfiguration });
};

export const getUscBasketConfiguration = (
    params: UscLocalStorageParams,
    vehicleForSaleId: string,
): BasketConfigurationType | null => {
    return getUscLocalStorage(params)?.basketConfigurations?.[vehicleForSaleId] || null;
};

export const getAllUscBasketConfigurations = (
    params: UscLocalStorageParams,
): { [vehicleForSaleId: string]: BasketConfigurationType } => {
    return getUscLocalStorage(params)?.basketConfigurations || {};
};

export const uscLocalStorageParamSelector = ({ commonSettings, vehicle }: UscStateType): UscLocalStorageParams => ({
    country: commonSettings.country,
    language: commonSettings.language,
    context: vehicle.type,
});
